'use client'
import NextLink from 'next/link'
import {
  HomePageControlIconName,
  IHomePageTemplateVideoBlock,
} from '@/types/firebase'
import { FC, useMemo, useState } from 'react'
import { ICommonIconProps } from '@/types/shared'
import { ArrowRadarIcon } from '@/components/icons/arrow-radar-icon'
import { CheckRoundedIcon } from '@/components/icons/check-rounded-icon'

const homepageTemplatesIconsObj: {
  [key in HomePageControlIconName]: React.FC<ICommonIconProps>
} = {
  [HomePageControlIconName.ArrowRadar]: ArrowRadarIcon,
  [HomePageControlIconName.CheckRounded]: CheckRoundedIcon,
  // TODO: next icons should be changed
  [HomePageControlIconName.Engineering]: ArrowRadarIcon,
  [HomePageControlIconName.Mathematics]: ArrowRadarIcon,
  [HomePageControlIconName.Science]: ArrowRadarIcon,
  [HomePageControlIconName.Technology]: ArrowRadarIcon,
}

interface Props {
  customContent: IHomePageTemplateVideoBlock
}

export const CommonVideoBlock: FC<Props> = ({ customContent }) => {
  const [hoveredElementId, setHoveredElementId] = useState<string>()

  const { link, subtitle, titlePt1, titlePt2, videoUrl } = customContent

  const IconComponent: FC<ICommonIconProps> | null = useMemo(() => {
    const iconName = link?.icon?.name

    if (!iconName) {
      return null
    }

    const icon = homepageTemplatesIconsObj[iconName]

    if (!icon) {
      return null
    }

    return icon
  }, [link?.icon?.name])

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="overflow-hidden py-10 px-6 relative rounded-[24px] bg-v8-blue shadow-v8-card flex flex-col-reverse items-center justify-center lg:px-20 lg:flex-row gap-4 shadow-v8-card">
        <div className="absolute  left-0 max-lg:bottom-[-31rem] lg:top-28 z-[10]">
          <svg
            width="600"
            height="810"
            viewBox="0 0 600 810"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              opacity="0.1"
              cx="195"
              cy="405"
              r="355"
              stroke="white"
              strokeWidth="100"
            />
          </svg>
        </div>

        <div className="flex justify-center items-center pb-0 pt-10 w-full lg:py-20 lg:w-[35%] z-[11]">
          <div className="flex flex-col items-center lg:items-start">
            <div className="flex flex-col items-center lg:items-start">
              {/* Title */}
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl text-center lg:text-left">
                {titlePt1 && (
                  <span
                    id="titlePt1"
                    className="block transition-colors"
                    style={{
                      fontSize: `${titlePt1.styles.fontSize}px`,
                      fontWeight: +titlePt1.styles.fontWeight,
                      lineHeight: `${titlePt1.styles.lineHeight}px`,
                      fontStyle: titlePt1.styles.isItalic ? 'italic' : 'normal',
                      color:
                        hoveredElementId === 'titlePt1'
                          ? titlePt1.styles.onHoverColor
                          : titlePt1.styles.color,
                    }}
                    onMouseEnter={() => setHoveredElementId('titlePt1')}
                    onMouseLeave={() => setHoveredElementId(undefined)}
                  >
                    {titlePt1.text}
                  </span>
                )}
                {titlePt2 && (
                  <span
                    id="titlePt2"
                    className="block transition-colors"
                    style={{
                      fontSize: `${titlePt2.styles.fontSize}px`,
                      fontWeight: +titlePt2.styles.fontWeight,
                      lineHeight: `${titlePt2.styles.lineHeight}px`,
                      fontStyle: titlePt2.styles.isItalic ? 'italic' : 'normal',
                      color:
                        hoveredElementId === 'titlePt2'
                          ? titlePt2.styles.onHoverColor
                          : titlePt2.styles.color,
                    }}
                    onMouseEnter={() => setHoveredElementId('titlePt2')}
                    onMouseLeave={() => setHoveredElementId(undefined)}
                  >
                    {titlePt2.text}
                  </span>
                )}
              </h2>

              {/* Subtitle */}
              {subtitle && (
                <p
                  id="subtitle"
                  className="mt-4 text-center lg:text-left w-3/4 transition-colors"
                  style={{
                    fontSize: `${subtitle.styles.fontSize}px`,
                    fontWeight: +subtitle.styles.fontWeight,
                    lineHeight: `${subtitle.styles.lineHeight}px`,
                    fontStyle: subtitle.styles.isItalic ? 'italic' : 'normal',
                    color:
                      hoveredElementId === 'subtitle'
                        ? subtitle.styles.onHoverColor
                        : subtitle.styles.color,
                  }}
                  onMouseEnter={() => setHoveredElementId('subtitle')}
                  onMouseLeave={() => setHoveredElementId(undefined)}
                >
                  {subtitle.text}
                </p>
              )}
            </div>

            {/* Link */}
            {link && (
              <NextLink
                id="link"
                href={link.href}
                target={link.openInNewWindow ? '_blank' : undefined}
                className="duration-300 mt-8 max-w-[200px] relative z-10 inline-flex items-center rounded-md border px-5 py-3 text-nowrap shadow transition duration-300 max-h-[50px]"
                style={{
                  fontSize: `${link.styles.fontSize}px`,
                  fontWeight: +link.styles.fontWeight,
                  lineHeight: `${link.styles.lineHeight}px`,
                  fontStyle: link.styles.isItalic ? 'italic' : 'normal',
                  color:
                    hoveredElementId === 'link'
                      ? link.styles.onHoverColor
                      : link.styles.color,
                  backgroundColor:
                    hoveredElementId === 'link'
                      ? link.styles.onHoverBackgroundColor
                      : link.styles.backgroundColor,

                  borderColor:
                    hoveredElementId === 'link'
                      ? link.styles.onHoverBorderColor || undefined
                      : link.styles.borderColor || undefined,
                }}
                onMouseEnter={() => setHoveredElementId('link')}
                onMouseLeave={() => setHoveredElementId(undefined)}
              >
                {link.text}

                {/* Icon */}
                {IconComponent && (
                  <span className="pl-3">
                    <IconComponent className="w-6 h-6" />
                  </span>
                )}
              </NextLink>
            )}
          </div>
        </div>

        {/* Video */}
        <div className="max-lg:w-full flex flex-col grow items-center justify-center z-[11]">
          <div className="relative w-full pb-[56.25%] h-[0px] overflow-hidden">
            <iframe
              src={videoUrl}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write;"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
              title="ExamSolution Image Upload Demo"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  )
}
